import {
  type CheckboxChangeEventType,
  DynamicFormRequiredType,
  type DynamicFormSettings,
} from '@myosh/odin-components';
import type { LocaleItem } from '../../../../@types/common';
import type { FormHierarchyTypes, HierarchyType } from '../../../../@types/hierarchy-fields';
import type { FormBuilerHierarchyType } from '../../../../redux/slices/form-builder-slice';
import { forceAssert } from '../../../../utils/type-guards';
import HfDropDown from '../../../fields/custom-drop-downs/hf-drop-down.component';
import { createTranslations } from '../../form-builder-utils';

const dataSettings = {
  valueField: 'value',
  textField: 'text',
};

export const advancedHierarchyFormSettings = (showAllValues: boolean): DynamicFormSettings => ({
  id: 'HierarchySettingsGroup',
  caption: '',
  fields: [
    {
      id: 'editable',
      fieldType: 'CHECKBOX',
      fieldName: 'editable',
      caption: 'Editable',
      dataSettings: dataSettings,
      required: DynamicFormRequiredType.False,
      permissions: { edit: true, read: true },
      position: 1,
    },
    {
      id: 'useInitialValue',
      fieldType: 'CHECKBOX',
      fieldName: 'useInitialValue',
      dataSettings: dataSettings,
      caption: 'Use Initial Value',
      required: DynamicFormRequiredType.False,
      permissions: { edit: true, read: true },
      position: 2,
    },
    {
      id: 'showAll',
      fieldType: 'CHECKBOX',
      fieldName: 'showAll',
      dataSettings: dataSettings,
      caption: 'Show All Values',
      required: DynamicFormRequiredType.False,
      permissions: { edit: true, read: true },
      position: 3,
    },
    {
      id: 'upperBoundHierarchyTypeId',
      fieldType: 'COMBOBOX',
      fieldName: 'upperBoundHierarchyTypeId',
      caption: 'Show All Values - Upper Limit',
      dataSettings: dataSettings,
      required: DynamicFormRequiredType.False,
      permissions: { edit: true, read: true },
      hidden: !showAllValues,
      position: 4,
    },
  ],
  fieldValueConverters: {
    CHECKBOX: (value) => {
      const convertedValue = forceAssert<CheckboxChangeEventType>(value);
      return convertedValue.checked;
    },
  },
  customFieldComponents: {
    COMBOBOX: HfDropDown,
  },
});

const DEFAULT_HIERARCHY_CONFIG = {
  useInitialValue: false,
  showAll: false,
  editable: false,
};

const createDefaultHierarchyField = (hierarchyType: HierarchyType, localeIds: LocaleItem[]) => ({
  caption: {
    translations: createTranslations(String(hierarchyType.caption ?? ''), localeIds),
  },
  hierarchyTypeConfigDTO: DEFAULT_HIERARCHY_CONFIG,
});

export const mergeHierarchyFields = (
  hierarchyTypes: HierarchyType[],
  formHierarchyTypes: FormHierarchyTypes[],
  localeIds: LocaleItem[]
): FormBuilerHierarchyType[] => {
  return hierarchyTypes.map((ht) => {
    const formConfig = formHierarchyTypes.find((f) => f.id === ht.id);
    const defaults = formConfig ? {} : createDefaultHierarchyField(ht, localeIds);
    return {
      ...ht,
      ...formConfig,
      ...defaults,
      id: ht.id,
      visible: !!formConfig,
      mandatoryTypeConfig: formConfig?.mandatoryTypeConfig ?? false,
    } as FormBuilerHierarchyType;
  });
};
